import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { webColors } from '../../constants/webStyles';

const SIBLING_COUNT = 2;

const Pagination = ({ currentPage, totalPages, totalItems, pageSize, onPageChange }) => {
  const isMobile = useIsMobile();
  if (totalPages <= 1) return null;

  const rangeStart = (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalItems);

  const pages = (() => {
    const result = [];
    const left = Math.max(2, currentPage - SIBLING_COUNT);
    const right = Math.min(totalPages - 1, currentPage + SIBLING_COUNT);

    result.push(1);
    if (left > 2) result.push('...');
    for (let i = left; i <= right; i++) result.push(i);
    if (right < totalPages - 1) result.push('...');
    if (right < totalPages) result.push(totalPages);

    return result;
  })();

  return (
    <View style={styles.container}>
      <View style={[styles.inner, isMobile && styles.innerMobile]}>
        {/* Previous nav group */}
        <View style={styles.navGroup}>
          <Pressable
            style={({ hovered }) => [
              styles.navBtn,
              currentPage === 1 && styles.navBtnDisabled,
              hovered && currentPage !== 1 && styles.navBtnHover,
            ]}
            accessibilityRole="button"
            accessibilityLabel="First page"
            onPress={() => currentPage > 1 && onPageChange(1)}
            disabled={currentPage === 1}
          >
            <ChevronsLeft size={15} color={currentPage === 1 ? '#d1d5db' : webColors.textSecondary} />
          </Pressable>
          <Pressable
            style={({ hovered }) => [
              styles.navBtn,
              currentPage === 1 && styles.navBtnDisabled,
              hovered && currentPage !== 1 && styles.navBtnHover,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Previous page"
            onPress={() => currentPage > 1 && onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={15} color={currentPage === 1 ? '#d1d5db' : webColors.textSecondary} />
          </Pressable>
        </View>

        {/* Separator */}
        {!isMobile && <View style={styles.separator} />}

        {/* Page numbers */}
        {isMobile ? (
          <Text style={styles.mobilePageInfo} accessibilityLiveRegion="polite">
            {currentPage} / {totalPages}
          </Text>
        ) : (
          <View style={styles.pagesGroup}>
            {pages.map((page, index) =>
              page === '...' ? (
                <Text key={`ellipsis-${index}`} style={styles.ellipsis}>...</Text>
              ) : (
                <Pressable
                  key={page}
                  style={({ hovered }) => [
                    styles.pageBtn,
                    page === currentPage && styles.pageBtnActive,
                    hovered && page !== currentPage && styles.pageBtnHover,
                  ]}
                  onPress={() => onPageChange(page)}
                >
                  <Text style={[
                    styles.pageText,
                    page === currentPage && styles.pageTextActive,
                  ]}>
                    {page}
                  </Text>
                </Pressable>
              )
            )}
          </View>
        )}

        {/* Separator */}
        {!isMobile && <View style={styles.separator} />}

        {/* Next nav group */}
        <View style={styles.navGroup}>
          <Pressable
            style={({ hovered }) => [
              styles.navBtn,
              currentPage === totalPages && styles.navBtnDisabled,
              hovered && currentPage !== totalPages && styles.navBtnHover,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Next page"
            onPress={() => currentPage < totalPages && onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={15} color={currentPage === totalPages ? '#d1d5db' : webColors.textSecondary} />
          </Pressable>
          <Pressable
            style={({ hovered }) => [
              styles.navBtn,
              currentPage === totalPages && styles.navBtnDisabled,
              hovered && currentPage !== totalPages && styles.navBtnHover,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Last page"
            onPress={() => currentPage < totalPages && onPageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            <ChevronsRight size={15} color={currentPage === totalPages ? '#d1d5db' : webColors.textSecondary} />
          </Pressable>
        </View>
      </View>

      <Text style={styles.info}>
        Showing <Text style={styles.infoHighlight}>{rangeStart}-{rangeEnd}</Text> of <Text style={styles.infoHighlight}>{totalItems}</Text> results
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'stretch',
    paddingVertical: 20,
    paddingHorizontal: 0,
    gap: 10,
  },
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    paddingVertical: 6,
    paddingHorizontal: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
  },
  innerMobile: {
    justifyContent: 'space-between',
  },
  mobilePageInfo: {
    flexShrink: 1,
    paddingHorizontal: 4,
    textAlign: 'center',
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  navGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  separator: {
    width: 1,
    height: 20,
    backgroundColor: webColors.border,
    marginHorizontal: 6,
  },
  pagesGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  navBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transitionDuration: '150ms',
    transitionProperty: 'background-color',
  },
  navBtnDisabled: {
    cursor: 'default',
  },
  navBtnHover: {
    backgroundColor: webColors.backgroundAlt,
  },
  pageBtn: {
    minWidth: 36,
    height: 34,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
    cursor: 'pointer',
    transitionDuration: '150ms',
    transitionProperty: 'background-color, color',
  },
  pageBtnActive: {
    backgroundColor: webColors.primary,
    shadowColor: webColors.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 2,
  },
  pageBtnHover: {
    backgroundColor: webColors.primaryLight,
  },
  pageText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  pageTextActive: {
    color: '#ffffff',
    fontWeight: '600',
  },
  ellipsis: {
    fontSize: 14,
    color: webColors.textMuted,
    paddingHorizontal: 4,
    letterSpacing: 2,
  },
  info: {
    fontSize: 13,
    color: webColors.textMuted,
    textAlign: 'center',
  },
  infoHighlight: {
    fontWeight: '600',
    color: webColors.textSecondary,
  },
});

export default Pagination;
